import { apiFetch, uploadFile } from './client';
import type {
  CatalogAnalytics,
  CatalogCategory,
  CatalogHealth,
  CatalogIssues,
  DuplicateGroup,
  ImportResult,
  MergePreview,
  Paginated,
  Product,
  ProductList
} from '$lib/types';

// UNCATEGORIZED is the ?category= sentinel the backend treats as "category
// = ''" (see uncategorizedSentinel in handlers/catalog.go) — "" itself can't
// be told apart from "no filter" on a query string.
export const UNCATEGORIZED = '__uncategorized__';

function toQuery(params: Record<string, string | number | boolean | undefined> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

export const catalogApi = {
  health: () => apiFetch<CatalogHealth>('/admin/catalog/health'),
  categories: () => apiFetch<{ results: CatalogCategory[]; count: number }>('/admin/catalog/categories'),
  products: (params?: {
    q?: string;
    family?: string;
    category?: string;
    ownership?: string;
    missing_taxonomy?: boolean;
    missing_price?: boolean;
    active?: boolean;
    page?: number;
    per_page?: number;
  }) => apiFetch<Paginated<Product>>(`/admin/catalog/products${toQuery(params)}`),

  bulkCategory: (product_ids: number[], category: string) =>
    apiFetch<{ moved: number }>('/admin/catalog/bulk/category', {
      method: 'POST',
      body: JSON.stringify({ product_ids, category })
    }),
  bulkOwnership: (product_ids: number[], ownership: string | null) =>
    apiFetch<{ moved: number }>('/admin/catalog/bulk/ownership', {
      method: 'POST',
      body: JSON.stringify({ product_ids, ownership })
    }),
  bulkTaxonomy: (
    product_ids: number[],
    body: { functional_category?: string; functional_type?: string }
  ) =>
    apiFetch<{ moved: number }>('/admin/catalog/bulk/taxonomy', {
      method: 'POST',
      body: JSON.stringify({ product_ids, ...body })
    }),

  renameCategory: (category: string, new_name: string) =>
    apiFetch<{ moved: number; category: string }>('/admin/catalog/categories/rename', {
      method: 'POST',
      body: JSON.stringify({ category, new_name })
    }),
  mergeCategory: (from: string, into: string) =>
    apiFetch<{ moved: number; from: string; into: string }>('/admin/catalog/categories/merge', {
      method: 'POST',
      body: JSON.stringify({ from, into })
    }),

  // Single-product inline edit reuses the existing admin product CRUD route
  // (see handlers/products.go's adminUpdateProduct) — no separate endpoint
  // needed for that.
  updateProduct: (id: number, body: Record<string, unknown>) =>
    apiFetch<Product>(`/admin/products/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  // Product-level dedup: duplicate detection by normalized part number, then
  // preview (line-item count for the confirm dialog) and commit.
  duplicates: () => apiFetch<{ results: DuplicateGroup[]; count: number }>('/admin/catalog/duplicates'),
  mergePreview: (winnerId: number, loserIds: number[]) =>
    apiFetch<MergePreview>('/admin/catalog/merge/preview', {
      method: 'POST',
      body: JSON.stringify({ winner_id: winnerId, loser_ids: loserIds })
    }),
  merge: (winnerId: number, loserIds: number[]) =>
    apiFetch<{ merged: number; line_items: number; winner_id: number }>('/admin/catalog/merge', {
      method: 'POST',
      body: JSON.stringify({ winner_id: winnerId, loser_ids: loserIds })
    }),

  // Cost-price review queue: active designer-created products still missing
  // base_cost_price_minor (see catalogReview in handlers/catalog_hygiene.go).
  review: () => apiFetch<ProductList>('/admin/catalog/review'),

  // Stale detection: active products with zero line-item usage on
  // quotations created within the window (default 6 months). Bulk archive
  // takes explicit product_ids, mirroring every other bulk op here.
  stale: (months?: number) => apiFetch<ProductList & { months: number }>(`/admin/catalog/stale${toQuery({ months })}`),
  bulkArchive: (product_ids: number[]) =>
    apiFetch<{ moved: number }>('/admin/catalog/bulk/archive', {
      method: 'POST',
      body: JSON.stringify({ product_ids })
    }),

  analytics: (days?: number) => apiFetch<CatalogAnalytics>(`/admin/catalog/analytics${toQuery({ days })}`),
  issues: () => apiFetch<CatalogIssues>('/admin/catalog/issues'),

  bulkRCU: (product_ids: number[], syncs_to_rcu: boolean) =>
    apiFetch<{ moved: number }>('/admin/catalog/bulk/rcu', {
      method: 'POST',
      body: JSON.stringify({ product_ids, syncs_to_rcu })
    }),

  // Same file re-sent to preview and commit so the confirm step commits
  // exactly what the preview showed; `force` applies conflict rows that
  // would otherwise be skipped (see catalogImportCommit's doc comment).
  importPreview: (file: File) => uploadFile<ImportResult>('/admin/catalog/import/preview', file),
  importCommit: (file: File, force?: boolean) =>
    uploadFile<ImportResult>('/admin/catalog/import/commit', file, force ? { force: 'true' } : undefined)
};
